<template>
  <div class="mt-2">
    <div v-show="showFilter || showSearch">
      <v-card class="pa-3 pt-0 base-card shadow">
        <v-row>
          <v-col cols="12">
            <UIAutocomplete
              v-model="selectedFilters.magazineId"
              item-text="name"
              item-value="id"
              :items="dynamicMagazinList"
              placeholder="Наименование издания: введите не менее 3-х символов"
              hide-details
              :loading="magazineListLoading"
              hide-no-data
              :return-object="false"
              @searchData="getMagazineList"
            />
          </v-col>
        </v-row>

        <v-row
          v-if="showFilter"
          class="d-flex align-center"
        >
          <v-col cols="4">
            <UIAutocomplete
              v-model="selectedFilters.statusId"
              item-text="name"
              item-value="id"
              placeholder="Статус"
              :no-filter="false"
              :items="dynamicStatusList"
              hide-details
              hide-no-data
              :return-object="false"
            />
          </v-col>
          <v-col cols="4">
            <UIAutocomplete
              v-model="selectedFilters.periodId"
              item-text="name"
              item-value="id"
              :items="dynamicQuarterList"
              placeholder="Квартал"
              hide-details
              hide-no-data
              :return-object="false"
            />
          </v-col>
          <v-col cols="4">
            <UIAutocomplete
              v-model="selectedFilters.departmentId"
              item-text="name"
              item-value="id"
              :items="dynamicDepartmentList"
              placeholder="Кафедра"
              hide-details
              hide-no-data
              :return-object="false"
            />
          </v-col>
          <v-col cols="4">
            <UIAutocomplete
              v-model="selectedFilters.roleId"
              :items="dynamicEditorialBoardRoleList"
              item-text="name"
              item-value="id"
              placeholder="Роль"
              hide-details
              hide-no-data
              :return-object="false"
            />
          </v-col>
          <v-col cols="4">
            <UIDatePickerMenu
              v-model="selectedFilters.acceptanceDate"
              :allowed-dates="date => dynamicFilters.acceptanceDates.includes(date)"
              placeholder="Дата принятия"
              hide-details
              hide-no-data
              :return-object="false"
            />
          </v-col>
        </v-row>
      </v-card>
    </div>
  </div>
</template>

<script>
import _debounce from 'lodash/debounce'
import { magazineApi } from '@/api'
import UIAutocomplete from '../ui/UIAutocomplete'
import UIDatePickerMenu from '../ui/UIDatePickerMenu'
import { MagazineParent } from '@/models'
import { dictionaryNames } from '@/scripts/consts'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { DynamicFilters } from '../../models/Filters'
import { journalEditorialBoardApi } from '../../api'
export default {
  name: 'JournalsFilter',
  components: {
    UIAutocomplete,
    UIDatePickerMenu
  },
  data: () => ({
    magazineList: new MagazineParent(),
    dynamicFilters: new DynamicFilters(),
    magazineListLoading: false,
    selectedFilters: {
      magazineId: null,
      statusId: null,
      periodId: null,
      departmentId: null,
      roleId: null,
      acceptanceDate: null
    }
  }),
  props: {
    showFilter: {
      type: Boolean,
      default: false
    },
    showSearch: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters('dictionaries', [
      'departmentList',
      'statusList',
      'quarterList',
      'editorialBoardRoleList',
      'periodList'
    ]),
    dynamicStatusList() {
      if (!this.statusList || !this.dynamicFilters.statusIds?.length) return []
      return this.statusList.filter(item => this.dynamicFilters.statusIds.includes(item.id))
    },
    dynamicQuarterList() {
      if (!this.periodList || !this.dynamicFilters.periodIds?.length) return []
      return this.periodList
        .filter(item => this.dynamicFilters.periodIds.includes(item.id))
        .map(item => {
          return {
            id: item.id,
            name: item.quarter.name + ' ' + item.year.year + ' г.'
          }
        })
    },

    dynamicDepartmentList() {
      if (!this.departmentList || !this.dynamicFilters.departmentIds?.length) return []
      return this.departmentList.filter(item => this.dynamicFilters.departmentIds.includes(item.id))
    },
    dynamicEditorialBoardRoleList() {
      if (!this.editorialBoardRoleList || !this.dynamicFilters.roleIds?.length) return []
      return this.editorialBoardRoleList.filter(item => this.dynamicFilters.roleIds.includes(item.id))
    },
    dynamicMagazinList() {
      if (!this.magazineList.list || !this.dynamicFilters.magazineIds?.length) return []
      return this.magazineList.list.filter(item => this.dynamicFilters.magazineIds.includes(item.id))
    }
  },
  async mounted() {
    if (this.showFilter && !this.dictionariesLoaded) {
      await this.getFilterParams()
    }
  },

  methods: {
    ...mapActions('dictionaries', ['getDictionaries']),
    async getFilterParams() {
      await this.getDictionaries([
        dictionaryNames.Department,
        dictionaryNames.Status,
        dictionaryNames.Quarter,
        dictionaryNames.EditorialBoardRole,
        dictionaryNames.Period
      ])
      await this.updateDynamicFilters()
    },
    async getDynamicFilters() {
      try {
        const filters = await journalEditorialBoardApi.findFilterAll(this.dynamicFilters)
        this.dynamicFilters = filters
      } catch (e) {
        console.error(e)
      }
    },
    async updateDynamicFilters() {
      try {
        const filters = await journalEditorialBoardApi.findFilterAll(DynamicFilters.buildFilters(this.selectedFilters))
        Object.assign(this.dynamicFilters, new DynamicFilters(filters))
      } catch (e) {
        console.error('Ошибка получения динамических фильтров:', e)
      }
    },
    getMagazineList: _debounce(async function (val) {
      try {
        if (!val) {
          this.magazineList = new MagazineParent()
          this.magazineListLoading = false
          return
        }

        if (val?.length >= 3 && this.publication?.magazine?.name !== val) {
          this.magazineListLoading = true
          const data = await magazineApi.findAllByName(val)
          const filteredData = (data || []).filter(item => this.dynamicFilters.magazineIds.includes(item.id))
          this.magazineList = new MagazineParent(filteredData)
          //   if (this.publication.magazine) this.magazineList.add(this.publication.magazine)
        }
        this.magazineListLoading = false
      } catch (e) {
        console.log(e)
        if (e) {
          this.magazineListLoading = false
          this.showErrorMessage(e, 'Не удалось получить список журналов')
        } else this.magazineListLoading = true
      }
    }, 500)
  },
  watch: {
    showFilter(val) {
      if (val && !this.dataLoaded) this.getFilterParams()
    },
    selectedFilters: {
      handler: _debounce(function () {
        const payloadForBackend = {
          magazineIds: this.selectedFilters.magazineId ? [this.selectedFilters.magazineId] : [],

          statusIds: this.selectedFilters.statusId ? [this.selectedFilters.statusId] : [],
          periodIds: this.selectedFilters.periodId ? [this.selectedFilters.periodId] : [],
          departmentIds: this.selectedFilters.departmentId ? [this.selectedFilters.departmentId] : [],
          roleIds: this.selectedFilters.roleId ? [this.selectedFilters.roleId] : [],
          acceptanceDates: this.selectedFilters.acceptanceDate ? [this.selectedFilters.acceptanceDate] : []
        }

        this.updateDynamicFilters()
        this.$emit('apply-filters', payloadForBackend)
      }, 500),
      deep: true
    }
  }
}
</script>

<style scoped></style>
